import React from "react";
import { fetchQuery, graphql } from "react-relay";

import { faCalendarRange } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { environment } from "~/models";
import toast, { ToastActionButton } from "~/ui/toast";

import type { locationStateActionItemLinkQuery } from "~/__generated__/locationStateActionItemLinkQuery.graphql";

export class OriginatingNoteState {
	constructor(public id: string) {}
}

export function encodeNoteLocation({ noteId, blockKey }: { noteId: string; blockKey?: string }): string {
	let buf = noteId;
	if (blockKey) buf += `/${blockKey}`;
	return buf;
}

export function decodeNoteLocation(raw: string):
	| {
			noteId: string;
			blockKey?: string;
			comments?: { threadKey: string };
			meetingRecording?: { id: string; timestamp?: number };
			isPrivateNote?: boolean;
			isMeetingBrief?: boolean;
			isAskCopilot?: boolean;
	  }
	| undefined {
	const [noteId, blockKey, section, argument] = raw.split("/");
	if (!noteId) return undefined;

	const result: ReturnType<typeof decodeNoteLocation> = { noteId };
	if (blockKey) result.blockKey = blockKey;
	if (section === "comments")
		result.comments = {
			threadKey: decodeURIComponent(argument),
		};
	else if (section === "recording") {
		const [id, timestamp] = argument.split("?ts=");
		if (id) {
			result.meetingRecording = { id };
			if (timestamp) {
				const parsedTimestamp = parseFloat(timestamp);
				if (!isNaN(parsedTimestamp)) result.meetingRecording.timestamp = parsedTimestamp;
			}
		}
	} else if (section === "private") {
		result.isPrivateNote = true;
	} else if (section === "brief") {
		result.isMeetingBrief = true;
	} else if (section === "ask-copilot") {
		result.isAskCopilot = true;
	}
	return result;
}

export function returnToast(currentStreamId: string | undefined, originatingNoteId: string) {
	return fetchQuery<locationStateActionItemLinkQuery>(
		environment,
		graphql`
			query locationStateActionItemLinkQuery($originatingNoteId: ID!) {
				node(id: $originatingNoteId) {
					... on Note {
						stream {
							id
							displayName
						}
					}
				}
			}
		`,
		{ originatingNoteId },
	)
		.toPromise()
		.then(data => {
			if (data?.node?.stream && data.node.stream.id !== currentStreamId) {
				const toastId = toast.info(
					<p>
						<FontAwesomeIcon icon={faCalendarRange} fixedWidth /> {data.node.stream.displayName}
					</p>,
					{
						noBorder: true,
						title: <>Return to stream:</>,
						actions: (
							<>
								<ToastActionButton
									variant="secondary"
									// eslint-disable-next-line react/jsx-no-bind
									onClick={() => toast.dismiss(toastId)}
								>
									Close
								</ToastActionButton>
								<ToastActionButton
									// eslint-disable-next-line react/jsx-no-bind
									onClick={() => {
										toast.dismiss(toastId);
										window.HISTORY.goBack();
									}}
								>
									Return
								</ToastActionButton>
							</>
						),
						autoClose: false,
						closeOnClick: false,
					},
				);
				const unlisten = window.HISTORY.listen(() => {
					unlisten();
					toast.dismiss(toastId);
				});
			}
		});
}
